import { HireFlowMenuItems } from "@/components/employee/HireFlowButton";
import { EmailSendModal } from "@/components/email/EmailSendModal.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EventEditModal } from "@/components/event/EventEditModal.tsx";
import {
  ConfirmationModal,
  useModals,
  Avatar,
  Box,
  Button,
  ButtonIcon,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSub,
  FontAwesomeIcon,
  Input,
  LinkTo,
  ListOption,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useFileUpload } from "@/hooks/use-file-upload.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { CandidateActions } from "@/pages/recruit/candidates/components/CandidateActions.tsx";
import { CandidateAIScore } from "@/pages/recruit/candidates/components/CandidateAIScore.tsx";
import { ScoreCardFeedbacksOverview } from "@/pages/recruit/candidates/components/ScoreCardFeedbacksOverview.tsx";
import { useCandidateToolbarOverflow } from "@/pages/recruit/candidates/components/use-candidate-toolbar-overflow.hook.ts";
import { ChangeStageModal } from "@/pages/recruit/candidates/widgets/ChangeStageModal.tsx";
import { OfferEditModal } from "@/pages/recruit/offers/widgets/OfferEditModal.tsx";
import { ScoreCardsRequestModal } from "@/pages/recruit/scorecards/widgets/ScoreCardsRequestModal.tsx";
import { TemplateMessageChooseModal } from "@/pages/settings/recruit/templates/widgets/TemplateMessageChooseModal.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useApplicantDetails } from "@/queries/recruit/use-applicants.query.ts";
import { useCandidateActionCreate } from "@/queries/recruit/use-candidate-actions.query.ts";
import { useCandidateDelete, useCandidateSnooze } from "@/queries/recruit/use-candidates.query.ts";
import { usePipelineById } from "@/queries/recruit/use-pipelines.query.ts";
import {
  useScoreCardRequestFeedback,
  useScoreCardSearchInline,
} from "@/queries/recruit/use-scorecards.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiActionPayload, ApiActionVisibilities } from "@/types/action.types.ts";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import {
  ApiEventAttendeeResponseStatuses,
  ApiEventEdit,
  ApiEventTypes,
  getMockEventEdit,
} from "@/types/event.types.ts";
import { ApiCandidate } from "@/types/recruit/applicant.types.ts";
import { getStageEmoji, PipelineStageTypes } from "@/types/recruit/stages.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { ReactElement, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export interface CandidateCardProps {
  candidate: ApiCandidate;
  isFirst: boolean;
  collapsed: boolean;
  applicantId: number;
}

type ToolbarItem = "event" | "upload" | "request_feedback";

function ToolbarTooltip({ label, children }: { label: string; children: ReactElement }) {
  return (
    <Tooltip delay={false}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function CandidateCard({ candidate, collapsed, isFirst, applicantId }: CandidateCardProps) {
  const { t } = useTranslation();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const modalsService = useModals();
  const navigate = useNavigate();
  const { renderRelativeDate } = useFormatter();
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const { data: employee } = useEmployeeMe();
  const { data: applicantFull } = useApplicantDetails(applicantId);
  const candidateDelete = useCandidateDelete();
  const candidateSnooze = useCandidateSnooze();
  const candidateActionCreate = useCandidateActionCreate();
  const canManageCandidates = canIEdit(ApiPermission.manage_candidates);
  const { data: pipeline } = usePipelineById(candidate.pipeline.id, canManageCandidates);
  const currentStageIndex =
    pipeline?.stages.findIndex((stage) => stage.id === candidate.pipeline_stage.id) ?? -1;
  const nextStage =
    candidate.disqualification_reason || currentStageIndex === -1
      ? undefined
      : pipeline?.stages[currentStageIndex + 1];
  const handleChangeStage = useCallback(() => {
    modalsService.openModal(ChangeStageModal, {
      pipelineId: candidate.pipeline.id,
      candidate: candidate,
    });
  }, [candidate]);

  const scoreCardSearchInline = useScoreCardSearchInline(
    isFeatureEnabled(ApiFeatureType.ats_scorecards)
  );
  const [isFeedbackMenuOpen, setIsFeedbackMenuOpen] = useState(false);

  const handleAttachFile = useCallback(
    (file: ApiFile) => {
      const payload: ApiActionPayload = {
        file_uuids: [file.uuid],
        visibility: ApiActionVisibilities.public,
      };
      candidateActionCreate.mutate(
        { id: candidate.id, payload },
        {
          onSuccess: () => {
            analyticsService.trackEvent(analyticEvents.candidates.addedAction, {
              [analyticProperties.id]: candidate.id,
              [analyticProperties.actionType]: "File Added",
              [analyticProperties.source]: "App",
            });
          },
        }
      );
    },
    [candidate]
  );

  const moveToNextStage = useCallback(() => {
    if (!nextStage) return;
    const payload: ApiActionPayload = {
      visibility: ApiActionVisibilities.public,
      comment: "",
      file_uuids: [],
      pipeline_stage_id: nextStage.id,
      datetime_action_date: new Date(),
    };
    candidateActionCreate.mutate(
      { id: candidate.id, payload },
      {
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.candidates.changedStage, {
            [analyticProperties.id]: candidate.id,
            [analyticProperties.pipelineStageTitle]: nextStage.title,
            [analyticProperties.count]: 1,
          });
        },
      }
    );
  }, [candidate, nextStage, candidateActionCreate]);

  const handleMoveToNextStage = useCallback(() => {
    if (!nextStage) return;
    const isFinalStage =
      nextStage.type === PipelineStageTypes.hired || nextStage.type === PipelineStageTypes.offer;
    if (!isFinalStage) {
      moveToNextStage();
      return;
    }
    modalsService.openModal(ConfirmationModal, {
      question: t("ats:move_to_next_stage"),
      text: t("ats:move_to_stage_confirm", { stage: nextStage.title }),
      acceptTitle: t("common:yes"),
      onAccept: moveToNextStage,
    });
  }, [nextStage, moveToNextStage, modalsService, t]);

  const fileUpload = useFileUpload({
    entityId: candidate.id,
    entityType: ApiEntityTypes.candidate,
    afterSend: handleAttachFile,
  });

  const handleSnooze = useCallback(() => {
    candidateSnooze.mutate(candidate.id, {
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.candidates.snoozed, {
          [analyticProperties.id]: candidate.id,
        });
      },
    });
  }, [candidate]);

  const handleDelete = useCallback(async () => {
    modalsService.openModal(ConfirmationModal, {
      question: t("ats:unassign_from_job_title"),
      text: t("ats:unassign_from_job_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          await candidateDelete.mutateAsync(candidate.id);
          analyticsService.trackEvent(analyticEvents.candidates.deleted, {
            [analyticProperties.id]: candidate.id,
          });
        } catch {
          return;
        }
      },
    });
  }, [candidate]);

  const handleSendEmail = useCallback(() => {
    modalsService.openModal(EmailSendModal, {
      singleEmail: {
        candidateId: candidate.id,
        applicantEmail: applicantFull?.contacts.email || "",
      },
    });
  }, [applicantFull]);

  const handleComposeMessage = useCallback(() => {
    modalsService.openModal(TemplateMessageChooseModal, {
      candidateId: candidate.id,
    });
  }, [applicantFull]);

  const handleAddEvent = useCallback(() => {
    if (employee && applicantFull) {
      const eventToEdit: ApiEventEdit = getMockEventEdit(
        employee,
        `${t("ats:interview")}: ${candidate.job.title} ${[
          applicantFull.first_name,
          applicantFull.middle_name,
          applicantFull.last_name,
        ]
          .filter((name) => name?.trim())
          .join(" ")}`
      );
      eventToEdit.type = ApiEventTypes.interview;
      eventToEdit.interview = {
        candidate_id: candidate.id,
        applicant_id: applicantFull.id,
        job_id: candidate.job.id,
        hide_salary: true,
      };
      if (applicantFull && applicantFull.contacts.email) {
        eventToEdit.suggestedAttendees = [
          {
            email: applicantFull.contacts.email,
            is_organizer: false,
            response_status: ApiEventAttendeeResponseStatuses.needs_action,
          },
        ];
      }

      modalsService.openModal(EventEditModal, {
        event: eventToEdit,
      });
    }
  }, [applicantFull, employee]);

  const canCreateOffer =
    canIEdit(ApiPermission.manage_offers) ||
    (isFeatureEnabled(ApiFeatureType.ats) && canIEdit(ApiPermission.manage_candidates));

  const canHire = canIEdit(ApiPermission.add_employees);

  const handleCreateOffer = useCallback(() => {
    if (!employee?.id) return;

    modalsService.openModal(OfferEditModal, {
      candidateId: candidate.id,
      applicant: applicantFull,
      employeeId: employee.id,
      jobId: candidate.job.id,
      onSave: () => {
        modalsService.openModal(ChangeStageModal, {
          pipelineId: candidate.pipeline.id,
          candidate: candidate,
        });
      },
    });
  }, [applicantFull, candidate, employee]);

  const handleRequestFeedback = useCallback(() => {
    if (!employee?.id) return;
    if (!applicantFull?.id) return;

    modalsService.openModal(ScoreCardsRequestModal, {
      candidateId: candidate.id,
      applicantId: applicantFull?.id,
      employeeId: employee.id,
      jobId: candidate.job.id,
    });
  }, [applicantFull, candidate, employee]);

  const requestFeedback = useScoreCardRequestFeedback();

  const handleScoreCardClick = useCallback(
    async (scoreCardId: number) => {
      if (requestFeedback.isPending) return;
      if (!employee?.id) return;
      if (!applicantFull?.id) return;

      setIsFeedbackMenuOpen(false);

      const feedback = await requestFeedback.mutateAsync({
        scoreCardId,
        candidateId: candidate.id,
      });

      analyticsService.trackEvent(analyticEvents.scoreCards.invitesSent, {
        [analyticProperties.id]: candidate.id,
      });

      if (!feedback.id) return;

      navigate(`/feedbacks/${feedback.id}/candidates/${candidate.id}`);
    },
    [applicantFull, candidate, employee, navigate, requestFeedback]
  );

  const hasScoreCards = isFeatureEnabled(ApiFeatureType.ats_scorecards);
  const toolbarItems: ToolbarItem[] = hasScoreCards
    ? ["event", "upload", "request_feedback"]
    : ["event", "upload"];
  const { rowRef, moreRef, registerFixed, visibleCount } = useCandidateToolbarOverflow(
    toolbarItems.length
  );
  const inlineItems = toolbarItems.slice(0, visibleCount);
  const menuItems = toolbarItems.slice(visibleCount);

  return (
    <Stack className={twMerge("py-6 px-6", isCollapsed ? undefined : "bg-light")}>
      <Stack gap={"md"} className={"pb-3"}>
        <Stack justify={"between"} direction={"horizontal"}>
          {candidate.disqualification_reason ? (
            candidate.reserved === true ? (
              <Avatar
                size={isFirst ? "xl" : "md"}
                url={null}
                emoji={emojiDefaults.stages.reserved}
                variant={"default"}
              />
            ) : (
              <Avatar
                size={isFirst ? "xl" : "md"}
                url={null}
                emoji={emojiDefaults.stages.disqualified}
                variant={"default"}
              />
            )
          ) : (
            <Avatar
              size={isFirst ? "xl" : "md"}
              url={null}
              variant={"default"}
              emoji={getStageEmoji(candidate.pipeline_stage)}
            />
          )}
          <Stack direction={"horizontal"}>
            <ButtonIcon
              variant={"plain"}
              onClick={() => {
                setIsCollapsed(!isCollapsed);
              }}
              icon={
                <FontAwesomeIcon
                  icon={twMerge("fa-light", isCollapsed ? "fa-chevron-down" : "fa-chevron-up")}
                />
              }
            />
          </Stack>
        </Stack>
        <Stack gap={"sm"}>
          <Stack>
            <Text size={"lg"} className={"text-title"}>
              {candidate.disqualification_reason == null ? (
                candidate.pipeline_stage.title
              ) : candidate.reserved === true ? (
                t("ats:action_reserved")
              ) : (
                <DotSeparator>
                  <Text>{t("ats:action_disqualified")}</Text>
                  <Text>{candidate.disqualification_reason.title}</Text>
                </DotSeparator>
              )}
            </Text>
            <Stack direction={"horizontal"}>
              <LinkTo
                variant={"secondary"}
                to={`/recruitment/candidates/?job_id=${candidate.job.id}`}
                className={"text-secondary flex gap-sm"}
              >
                {`${candidate.job.title}`}
              </LinkTo>
            </Stack>
          </Stack>
          {isCollapsed && candidate.last_action && (
            <Text>{`${t("common:updated")} ${renderRelativeDate(candidate.last_action.datetime_action_date)}`}</Text>
          )}
        </Stack>
      </Stack>
      {!isCollapsed && (
        <Stack gap={"smd"} className={"border-b border-dark/10 pb-4"}>
          {candidate.stage_is_overdue && (
            <Box>
              <Text className={"text-warning font-semibold"}>
                {`${t("ats:overdue")}: ${t("plurals:plural_day", { count: candidate.business_days_on_stage ?? 0 })}`}
              </Text>
              <Text>{" · "}</Text>
              <Text className={"text-secondary"}>
                {`${t("ats:overdue_standard_time")} ${t("plurals:plural_day", { count: candidate.stage_time_limit })}.`}
              </Text>
            </Box>
          )}
          {(canManageCandidates || hasScoreCards) && (
            <Stack ref={rowRef} direction={"horizontal"} gap={"sm"} className={"flex-wrap"}>
              {canManageCandidates && (
                <>
                  {fileUpload.input}
                  <Box ref={registerFixed}>
                    <Button
                      variant={"primary"}
                      onClick={handleChangeStage}
                      leftIcon={<FontAwesomeIcon icon={"fa-light fa-right-left-large"} />}
                    >
                      {t("ats:change_stage")}
                    </Button>
                  </Box>
                </>
              )}
              {hasScoreCards && (
                <Box ref={registerFixed}>
                  <DropdownMenu
                    open={isFeedbackMenuOpen}
                    onOpenChange={(open) => {
                      setIsFeedbackMenuOpen(open);
                      if (!open) {
                        scoreCardSearchInline.search(undefined);
                      }
                    }}
                    trigger={
                      <Button
                        variant={"secondary"}
                        leftIcon={<FontAwesomeIcon icon="fa-light fa-message-lines" />}
                      >
                        {t("scorecards:feedback")}
                      </Button>
                    }
                  >
                    <Stack gap={"xs"} className={"w-64"}>
                      <Box
                        className={"sticky top-0 z-10 bg-light px-2 pb-2"}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key !== "Escape") {
                            e.stopPropagation();
                          }
                        }}
                      >
                        <Input
                          type={"text"}
                          label={`${t("scorecards:search_forms")}...`}
                          autoFocus={false}
                          onInput={(e) => {
                            scoreCardSearchInline.search(e.currentTarget.value);
                          }}
                        />
                      </Box>
                      {scoreCardSearchInline.isLoading ? (
                        <Stack gap={"xs"}>
                          <Box className={"px-2 py-1"}>
                            <Skeleton className={"w-full h-5 rounded-xs"}></Skeleton>
                          </Box>
                          <Box className={"px-2 py-1"}>
                            <Skeleton className={"w-full h-5 rounded-xs"}></Skeleton>
                          </Box>
                        </Stack>
                      ) : (
                        <Stack gap={"xs"}>
                          {scoreCardSearchInline.data?.items.map((scorecard) => (
                            <ListOption
                              key={`request-button-${scorecard.id}`}
                              title={scorecard.title ?? "-"}
                              onClick={() => {
                                handleScoreCardClick(scorecard.id);
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                      {canIEdit(ApiPermission.ats_manage_scorecards) && (
                        <Box
                          className={
                            "sticky bottom-0 -mx-2 border-t border-dark/5 bg-light px-2 py-1"
                          }
                        >
                          <DropdownMenuItem
                            onClick={() => window.open("/recruitment/scorecards", "_blank")}
                            leftIcon={<FontAwesomeIcon icon="fa-light fa-gear" />}
                            title={t("scorecards:manage_forms")}
                          />
                        </Box>
                      )}
                    </Stack>
                  </DropdownMenu>
                </Box>
              )}
              {canManageCandidates && (
                <>
                  <Box ref={registerFixed}>
                    <DropdownMenu
                      trigger={
                        <ToolbarTooltip label={t("core:send")}>
                          <ButtonIcon
                            variant={"secondary"}
                            icon={<FontAwesomeIcon icon={"fa-light fa-paper-plane text-lg"} />}
                          />
                        </ToolbarTooltip>
                      }
                    >
                      <DropdownMenuItem
                        onClick={handleSendEmail}
                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-envelope"} />}
                        title={t("core:send_email")}
                      />
                      <DropdownMenuItem
                        onClick={handleComposeMessage}
                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-paper-plane"} />}
                        title={t("core:create_message")}
                      />
                    </DropdownMenu>
                  </Box>
                  {inlineItems.includes("event") && (
                    <ToolbarTooltip label={t("calendar:event_create_title")}>
                      <ButtonIcon
                        onClick={() => handleAddEvent()}
                        icon={<FontAwesomeIcon icon={"fa-light fa-calendar-plus text-lg"} />}
                        variant={"secondary"}
                      />
                    </ToolbarTooltip>
                  )}
                  {inlineItems.includes("upload") && (
                    <ToolbarTooltip label={t("common:upload_file")}>
                      <ButtonIcon
                        variant={"secondary"}
                        onClick={fileUpload.openPicker}
                        icon={
                          fileUpload.isLoading ? (
                            <FontAwesomeIcon icon="fa-light fa-spinner fa-spin" />
                          ) : (
                            <FontAwesomeIcon icon={"fa-light fa-paperclip text-lg"} />
                          )
                        }
                      />
                    </ToolbarTooltip>
                  )}
                  {inlineItems.includes("request_feedback") && (
                    <ToolbarTooltip label={t("scorecards:request_feedback")}>
                      <ButtonIcon
                        variant={"secondary"}
                        onClick={handleRequestFeedback}
                        icon={<FontAwesomeIcon icon="fa-light fa-message-lines text-lg" />}
                      />
                    </ToolbarTooltip>
                  )}
                  <Box ref={moreRef}>
                    <DropdownMenu
                      trigger={
                        <ToolbarTooltip label={t("common:more")}>
                          <ButtonIcon
                            variant={"secondary"}
                            icon={<FontAwesomeIcon icon={"fa-light fa-ellipsis"} />}
                          />
                        </ToolbarTooltip>
                      }
                    >
                      {menuItems.includes("event") && (
                        <DropdownMenuItem
                          onClick={handleAddEvent}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-calendar-plus" />}
                          title={t("calendar:event_create_title") + "..."}
                        />
                      )}
                      {menuItems.includes("upload") && (
                        <DropdownMenuItem
                          onClick={fileUpload.openPicker}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-paperclip" />}
                          title={t("common:upload_file") + "..."}
                        />
                      )}
                      {menuItems.includes("request_feedback") && (
                        <DropdownMenuItem
                          onClick={handleRequestFeedback}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-message-lines" />}
                          title={t("scorecards:request_feedback") + "..."}
                        />
                      )}
                      {canCreateOffer && (
                        <DropdownMenuItem
                          onClick={handleCreateOffer}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-file-contract" />}
                          title={t("ats:create_offer") + "..."}
                        />
                      )}
                      {canHire && (
                        <DropdownMenuSub
                          title={t("ats:hire_candidate")}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-heart-circle-check" />}
                        >
                          <HireFlowMenuItems candidateId={candidate.id} />
                        </DropdownMenuSub>
                      )}
                      {nextStage && (
                        <DropdownMenuItem
                          onClick={handleMoveToNextStage}
                          disabled={candidateActionCreate.isPending}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-right" />}
                          title={t("ats:move_to_next_stage")}
                          description={nextStage.title}
                        />
                      )}
                      {candidate.stage_is_overdue && (
                        <DropdownMenuItem
                          onClick={handleSnooze}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-clock" />}
                          title={t("common:snooze")}
                        />
                      )}
                      <DropdownMenuItem
                        onClick={handleDelete}
                        variant={"menuDanger"}
                        leftIcon={<FontAwesomeIcon icon="fa-light fa-xmark" />}
                        title={t("ats:unassign_from_job_title") + "..."}
                      />
                    </DropdownMenu>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </Stack>
      )}
      {!isCollapsed && (
        <ScoreCardFeedbacksOverview
          candidateId={candidate.id}
          isCollapsed={isCollapsed}
          applicantId={applicantId}
        />
      )}
      {isFeatureEnabled(ApiFeatureType.ats_ai) && !isCollapsed && (
        <CandidateAIScore candidate={candidate} />
      )}
      <CandidateActions
        candidate={candidate}
        collapsed={isCollapsed}
        applicantId={applicantId}
        isFirst={isFirst}
      />
    </Stack>
  );
}
